import React, { useEffect, useState } from 'react';
import { useApi } from '@/hooks/useApi';
import { useProfile } from '@/context';
import ApiService from '../services/api'
import queryString from 'query-string';
import dayjs from 'dayjs';
import { formatRupiah, formatRupiahTerbilang } from '@/helpers/utils';

const LoanPaymentModal = ({ isOpen, onClose, onSubmit, title, dataLoan, schedule }) => {
  if (!isOpen) return null;

  const api = useApi();
  const params = queryString.parse(window.location.search);
  const id = params.id || null
  const { profile } = useProfile();
  const cooperativeId = profile?.cooperative?.id
  const [paymentList, setPaymentList] = useState([])
  const [firstSchedule, setFirstSchedule] = useState({})
  const [debitAccounts, setDebitAccounts] = useState([])
  const [creditAccounts, setCreditAccounts] = useState([])
  const [loanProduct, setLoanProduct] = useState(null)
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    notes: '',
    member_id: '',
    payment_date: '',
    payment_details: '',
    payment_method_id: '',
    payment_reference: '',
    payment_type: '',
    receipt_number: '',
    installment_number: 0,
    loan_id: 0,
    debit_account_id: '',
    credit_account_id: '',
  });
  
  useEffect(() => {
    fetchPayments();
    fetchLoanProduct();
  }, [cooperativeId]);
    
  useEffect(() => {
    if(schedule.length > 0){
        const firstPendingSchedule = schedule.find(item => item.status === 'pending');
        
        if (firstPendingSchedule) {
            setFirstSchedule(firstPendingSchedule);
        } else {
            console.log('Tidak ada jadwal dengan status pending.');
        }
    }
  },[dataLoan,schedule])

  const fetchLoanProduct = async () => {
    if (dataLoan?.loan_product_id) {
      try {
        const response = await ApiService.getLoanProductById(dataLoan.loan_product_id);
        if (response && response.data) {
          setLoanProduct(response.data);
          // Auto-populate COA accounts from loan product settings
          if (response.data.cash_account && response.data.loan_receivable_account) {
            setFormData(prev => ({
              ...prev,
              debit_account_id: response.data.cash_account,
              credit_account_id: response.data.loan_receivable_account,
            }));
          }
        }
      } catch (error) {
        console.error("Failed to fetch loan product:", error);
      }
    }
  };

  const fetchChartOfAccounts = async () => {
    if (cooperativeId) {
      try {
        // Fetch debit accounts (ASSET - for payment: debit kas/bank)
        const debitResponse = await ApiService.getChartOfAccountsByType(
          cooperativeId,
          'ASSET',
          'CURRENT_ASSET'
        );
        setDebitAccounts(debitResponse?.data || []);

        // Fetch credit accounts (ASSET - for payment: credit piutang pinjaman)
        const creditResponse = await ApiService.getChartOfAccountsByType(
          cooperativeId,
          'ASSET',
          'CURRENT_ASSET'
        );
        setCreditAccounts(creditResponse?.data || []);
      } catch (error) {
        console.error("Failed to fetch chart of accounts:", error);
        setDebitAccounts([]);
        setCreditAccounts([]);
      }
    }
  };

  useEffect(() => {
    // Only fetch COA list if loan product doesn't have COA configured
    if (cooperativeId && (!loanProduct || !loanProduct.cash_account || !loanProduct.loan_receivable_account)) {
      fetchChartOfAccounts();
    }
  }, [cooperativeId, loanProduct]);

  useEffect(() => {
    switch (formData.payment_method_id) {
      case '42':
        setFormData(prevData => ({
          ...prevData,
          payment_details: "{\"payment_type\": \"cash\", \"received_by\": \"Kasir Koperasi\"}"
        }));
        break;
      case '43':
        setFormData(prevData => ({
          ...prevData,
          payment_details: "{\"payment_type\": \"bank_transfer\", \"bank_name\": \"Bank BCA\", \"account_number\": \"1234567890\", \"transfer_date\": \"2025-08-11\"}"
        }));
        break;
      case '44':
        setFormData(prevData => ({
          ...prevData,
          payment_details: "{\"payment_type\": \"debit_card\", \"card_number\": \"**1234\", \"bank_name\": \"Bank Mandiri\"}"
        }));
        break;
      case '45':
        setFormData(prevData => ({
          ...prevData,
          payment_details: "{\"payment_type\": \"QRIS\", \"received_by\": \"Kasir Koperasi\"}"
        }));
        break;
      default:
        break;
    }

  },[formData.payment_method_id])

  const fetchPayments = async () => {
    if (cooperativeId) {
      try {
        const payments = await api.get('/cashier/payment-methods');
        
        if (payments && payments.data && typeof payments.data === 'object') {
          setPaymentList(payments.data);
        } else {
          setPaymentList([]);
        }
      } catch (error) {
        console.error("Failed to fetch payments:", error);
        setPaymentList([]);
      }
    }
  };
  
  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: files ? files[0] : value, 
    }));
    
    setFormData(prevData => ({
        ...prevData,
        description: '-',
        amount: firstSchedule?.total_installment,
        notes: 'Pembayaran Pinjaman Bunga + Pokok',
        member_id: dataLoan?.member?.id,
        payment_date: dayjs().format(),
        payment_reference: '',
        payment_type: 'regular',
        receipt_number: '',
        installment_number: firstSchedule?.installment_number,
        loan_id: firstSchedule?.loan_id,
    }))
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if(formData.payment_method_id !== ''){
        onSubmit(formData);
        onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>

      {/* Modal Container */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative w-full max-w-lg max-h-full bg-white rounded-lg shadow-lg">
          
          {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h5 className="text-xl font-semibold text-gray-800">
                    {title}
                </h5>
                <button
                    type="button"
                    className="text-gray-400 hover:text-gray-900"
                    onClick={onClose}
                >
                    <i className='mgc_close_line'></i>
                </button>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="p-6 space-y-4">
                  <div className="text-sm space-y-2">
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Tanggal Transaksi
                      </label>
                      <p className='block text-sm text-gray-500'>
                        {dayjs().format('DD/MM/YYYY')}
                      </p>
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Jenis Pinjaman
                      </label>
                      <p className='block text-sm text-gray-500'>
                        {dataLoan?.loan_product?.name}
                      </p>
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Angsuran
                      </label>
                      <p className='block text-sm text-gray-500'>
                        Bulan {firstSchedule?.installment_number}
                      </p>
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Nama Penyetor
                      </label>
                      <p className='block text-sm text-gray-500'>
                        {dataLoan?.member?.full_name}
                      </p>
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Pembayaran Pokok
                      </label>
                      <p className='block text-sm text-gray-500'>
                        {formatRupiah(firstSchedule.principal_amount)}
                      </p>
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Pembayaran Bunga
                      </label>
                      <p className='block text-sm text-gray-500'>
                        {formatRupiah(firstSchedule.interest_amount)}
                      </p>
                    </div>
                    <hr />
                    <div>
                      <label className='block text-sm font-medium text-gray-700'>
                        Jumlah Setoran
                      </label>
                      <p className='block text-sm text-gray-500'>
                        {formatRupiah(firstSchedule.total_installment)}
                      </p>
                      <p className='block text-sm text-gray-800 mt-1'>
                        {formatRupiahTerbilang(firstSchedule.total_installment)}
                      </p>
                    </div>
                  </div>
                          
                    {/* Payments ID */}
                    <div>
                        <label htmlFor="payment_method_id" className="block text-sm font-medium text-gray-700">Payment Method</label>
                        <select
                            id="payment_method_id"
                            name="payment_method_id"
                            value={formData.payment_method_id}
                            onChange={handleChange}
                            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                        >
                        <option value="">Select Payment</option>
                        {paymentList?.map((res) => (
                            <option key={res.id} value={res.id}>
                                {res.name}
                            </option>
                        ))}
                        </select>
                    </div>

                    {/* COA Information or Manual Selection */}
                    {loanProduct && loanProduct.cash_account && loanProduct.loan_receivable_account ? (
                      // Show COA info if configured in loan product
                      <div className="bg-green-50 border border-green-200 rounded-md p-4">
                        <h5 className="text-sm font-semibold text-green-800 mb-2">
                          <i className="mgc_check_circle_line mr-1"></i>
                          Akun Jurnal (Auto dari Produk Pinjaman)
                        </h5>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-600">Akun Debit (Kas/Bank):</span>
                            <p className="font-medium text-gray-900">{loanProduct.cash_account}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Akun Kredit (Piutang):</span>
                            <p className="font-medium text-gray-900">{loanProduct.loan_receivable_account}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Show manual selection if COA not configured
                      <>
                        {/* Debit Account */}
                        <div>
                          <label htmlFor="debit_account_id" className="block text-sm font-medium text-gray-700">
                            Akun Debit (Kas/Bank) <span className="text-red-500">*</span>
                          </label>
                          <select
                              id="debit_account_id"
                              name="debit_account_id"
                              value={formData.debit_account_id}
                              onChange={handleChange}
                              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              required
                          >
                            <option value="">Pilih Akun Debit</option>
                            {debitAccounts?.map((account) => (
                              <option key={account.id} value={account.id}>
                                {account.account_code} - {account.account_name}
                              </option>
                            ))}
                          </select>
                          <small className="text-yellow-600 text-xs mt-1 block">
                            <i className="mgc_alert_line mr-1"></i>
                            COA belum di-set di produk pinjaman. Silakan pilih manual.
                          </small>
                        </div>

                        {/* Credit Account */}
                        <div>
                          <label htmlFor="credit_account_id" className="block text-sm font-medium text-gray-700">
                            Akun Kredit (Piutang Pinjaman) <span className="text-red-500">*</span>
                          </label>
                          <select
                              id="credit_account_id"
                              name="credit_account_id"
                              value={formData.credit_account_id}
                              onChange={handleChange}
                              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              required
                          >
                            <option value="">Pilih Akun Kredit</option>
                            {creditAccounts?.map((account) => (
                              <option key={account.id} value={account.id}>
                                {account.account_code} - {account.account_name}
                              </option>
                            ))}
                          </select>
                          <small className="text-yellow-600 text-xs mt-1 block">
                            <i className="mgc_alert_line mr-1"></i>
                            COA belum di-set di produk pinjaman. Silakan pilih manual.
                          </small>
                        </div>
                      </>
                    )}
                </div>

                {/* Modal Footer (Form Actions) */}
                <div className="flex justify-end p-4 border-t border-gray-200">
                    <button
                        type="button"
                        className="btn mr-2"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                        Submit Payment
                    </button>
                </div>
            </form>

        </div>
      </div>
    </div>
  );
};

export default LoanPaymentModal;